import { type PermissionType } from '@featit/shared/access-control';
import { credentialDisplayName } from '@featit/shared/credentialClient';
import { type CredentialAudienceType, type CredentialKindType } from '@featit/shared/credentials';
import { type McpClientKindType } from '@featit/shared/mcp-connect';
import { ApiService, decodedAccessTokenClaims } from './ApiService';

/**
 * A credential as `/auth/credentials` returns it — `AuthService.publicCredential`, field for field,
 * with the timestamps as the ISO strings JSON gives them.
 *
 * Written to the server's shape rather than a shape convenient for the screen. The list is a
 * security surface: the day these disagree, the screen is describing a credential the deployment
 * does not have.
 */
export interface ICredential {
  id: string;
  kind: CredentialKindType;
  audience: CredentialAudienceType;
  /**
   * The agent an `agent` credential acts as (I-0613); null for every other kind. Absent from servers
   * older than the agent-credential mint route.
   */
  agentId?: string | null;
  /**
   * I-0645, AC-CS10: set when an OAuth grant minted this credential (a cloud connector), null when
   * the person made it on Connected assistants. Absent from servers older than the one list.
   */
  oauthClientId?: string | null;
  /**
   * F-0332: a mobile device connected by QR code, whose session renews only with its own key's
   * signature. Absent from servers older than pairing.
   */
  pairedDevice?: boolean;
  label: string | null;
  /**
   * The permissions declared when the credential was minted; `null` means the kind's full ceiling.
   *
   * Carried because the payload carries it, and read only by {@link credentialAuthority} — never
   * rendered as a list. Nothing on the request path intersects it yet (ADR-051 §5, S3), so printing
   * the permissions would tell the reader they had bounded a credential where no bound exists.
   */
  scope: PermissionType[] | null;
  userAgent: string | null;
  /**
   * I-1034: what the sign-in said about its machine ("Jay’s MacBook Pro", "iPhone18,1") and the
   * addresses it was signed in and last used from. Absent from older servers; the addresses are
   * cleared once the credential ends.
   */
  deviceName?: string | null;
  deviceModel?: string | null;
  createdIp?: string | null;
  lastUsedIp?: string | null;
  /** I-1034 AC-6: the Mac app's interface hardware address and that interface's name. */
  deviceMac?: string | null;
  deviceMacInterface?: string | null;
  /** F-0555: whether the Mac app was let read that address; `null` where nothing was asked. */
  deviceMacConsent?: 'granted' | 'withheld' | null;
  lastUsedAt: string | null;
  rotatedAt: string | null;
  revokedAt: string | null;
  /**
   * Why, where the server knows and it matters (I-0394): `refresh-secret-reuse` means it revoked the
   * credential itself because a secret it had rotated past came back. Absent from older servers.
   */
  revokedReason?: string | null;
  expiresAt: string;
  createdAt: string;
}

/** The server's reason for a credential it revoked after one of its retired secrets came back. */
export const REVOKED_FOR_REFRESH_SECRET_REUSE = 'refresh-secret-reuse';

/** Where a credential is in its life. Derived, never stored — the server sends the two dates. */
export const CREDENTIAL_STATES = ['active', 'revoked', 'expired'] as const;
export type CredentialStateType = (typeof CREDENTIAL_STATES)[number];

/**
 * Revoked beats expired: a credential a person revoked and which then expired should still say
 * "revoked", because that is the fact they went looking for when they opened this screen.
 */
export function credentialState(credential: ICredential, now = Date.now()): CredentialStateType {
  if (credential.revokedAt) return 'revoked';
  return new Date(credential.expiresAt).getTime() <= now ? 'expired' : 'active';
}

/**
 * The name a person gave it, or where it was signed in — "FEATit for Mac", "Safari on iPhone"
 * (I-1034). Derived when read, so sessions minted before the name existed are named too. Never blank
 * — see ADR-051 §5.
 */
export function credentialLabel(credential: ICredential): string {
  return credentialDisplayName(credential);
}

/**
 * What this credential lets its holder do, said honestly.
 *
 * ADR-051 exists to make a credential's bound visible, and the honest answer for everything this
 * deployment mints and still honours is that there is no bound: `ACCEPTED_CREDENTIAL_KINDS` is
 * `['session']`, a `session` credential's ceiling is the whole permission vocabulary, and every
 * credential is minted with `scope: null`. Saying so is the fact worth showing — a browser session
 * forgotten on a shared machine carries the whole of your authority, and the list that hid that
 * would be the useless one.
 *
 * A spent credential gets the narrower sentence rather than the same one greyed out, because the
 * ceiling stops describing it the moment it stops being honoured. And the sentence is "cannot
 * renew", not "carries nothing": revoking stops the refresh and does not reach back into an access
 * token already minted from it (ADR-051 §5). "Carries nothing" is the tidier claim and the wrong one.
 *
 * The last branch is not defensive decoration. The web app can be pointed at any deployment
 * (ADR-050), including one running a build that mints narrower kinds, and it must not answer for a
 * credential it does not understand by claiming the widest thing. What it will not do either is
 * name the permissions: the intersection is not on the request path in this build, so a scope here
 * is a declaration and not yet a boundary.
 */
export function credentialAuthority(credential: ICredential, now = Date.now()): string {
  const state = credentialState(credential, now);
  if (state === 'revoked') return 'Revoked — it cannot renew';
  if (state === 'expired') return 'Expired — it cannot renew';
  if (credential.kind === 'session' && credential.scope === null) {
    return 'Carries everything you can do';
  }
  return 'Declares a narrower scope than you hold';
}

/**
 * Which credential this browser is presenting, read from the `cid` claim of the token in hand.
 *
 * Unverified, and it does not need to be: nothing is authorised by this. It decides which entry
 * gets a badge and which confirmation warns you that you are about to sign yourself out. A tampered
 * token would mislabel a list and change nothing about what the server permits — and the request
 * carrying it would be refused before the list ever arrived.
 */
export function currentCredentialId(): string | null {
  const cid = decodedAccessTokenClaims()?.cid;
  return typeof cid === 'string' && cid ? cid : null;
}

/**
 * What `POST /auth/agent-credentials` answers (I-0613; the contract is in
 * `tasks/2026-09-23-mcp-usable-end-to-end.md`). The two secrets exist in this object and nowhere
 * else: the server cannot show either again.
 */
export interface IMintedAgentCredential {
  credential: ICredential;
  accessToken: string;
  accessTokenExpiresAt: string | null;
  refreshToken: string;
}

/** U-0043: what "Revoke everything not used in the last … days" would end. */
export interface IUnusedCredentialsPreview {
  cutoff: string;
  credentials: Array<{
    id: string;
    kind: CredentialKindType;
    label: string | null;
    lastUsedAt: string | null;
    createdAt: string;
  }>;
}

class CredentialApiService extends ApiService {
  list(): Promise<ICredential[]> {
    return this.get('/auth/credentials');
  }

  rename(id: string, label: string | null): Promise<ICredential> {
    return this.patch(`/auth/credentials/${encodeURIComponent(id)}`, { label });
  }

  mintAgentCredential(input: {
    agentId: string;
    clientKind?: McpClientKindType | null;
    label?: string | null;
  }): Promise<IMintedAgentCredential> {
    return this.post('/auth/agent-credentials', {
      agentId: input.agentId,
      ...(input.clientKind ? { clientKind: input.clientKind } : {}),
      ...(input.label?.trim() ? { label: input.label.trim() } : {}),
    });
  }

  revoke(id: string): Promise<ICredential> {
    return this.delete(`/auth/credentials/${encodeURIComponent(id)}`);
  }

  /** I-1034 AC-3: every other signed-in session ends; this one, and agent and bridge keys, do not. */
  signOutOthers(): Promise<{ signedOut: number }> {
    return this.post('/auth/credentials/sign-out-others', {});
  }

  /**
   * U-0043: every live credential not used in the last `unusedForDays` days, never this one. Reads
   * only; `revokeUnused` revokes what the person confirmed of it.
   */
  previewUnused(unusedForDays: number): Promise<IUnusedCredentialsPreview> {
    return this.post('/auth/credentials/revoke-unused/preview', { unusedForDays });
  }

  /** U-0043: revoke the previewed credentials that still qualify, each journalled on its own. */
  revokeUnused(
    unusedForDays: number,
    credentialIds: string[],
  ): Promise<{ revoked: number; skipped: number }> {
    return this.post('/auth/credentials/revoke-unused', { unusedForDays, credentialIds });
  }

  /** F-0555 AC-4: the server forgets this install's network interface address at once. */
  withdrawMacConsent(): Promise<{ cleared: number }> {
    return this.post('/auth/credentials/mac-consent/withdraw', {});
  }
}

export const credentialApi = new CredentialApiService();
